/**
 * Plan of Record — workspace shell for a single PoR.
 *
 * Loads the fully composed PoR via assemble_por and hydrates the dashboard,
 * the section editor, and the Change Log from that single object. `refresh`
 * re-runs assemble_por after any mutation so every surface stays in sync.
 */

import { useCallback, useEffect, useState } from 'react';
import { Routes, Route, Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { assemblePor } from '../../lib/planOfRecordService';
import {
  instrumentSubpathFromPathname,
  planNarrativeBasePath,
} from '../../lib/prioritizationPlanInstrumentPath';
import { usePlanNarrativeEnabled } from '../../hooks/usePlanNarrativeEnabled';
import type { AssembledPor } from '../../types/plan-of-record';
import { PorLayout, PorSidebar } from '../../components/plan-of-record/PorShell';
import PorDashboard from './PorDashboard';
import PorSectionView from './PorSectionView';
import PorChangeLog from './PorChangeLog';
import PorStrategicPriorities from './PorStrategicPriorities';
import PorSynopsis from './PorSynopsis';
import PorSponsorBrief from './PorSponsorBrief';
import PorOfficialRecord from './PorOfficialRecord';
import { usePlanInstrumentNav } from '../../contexts/PlanInstrumentNavContext';
import { PorVisiblePage } from '../../components/plan-of-record/PorVisiblePage';
import { PorDefaultRedirect } from '../../components/plan-of-record/PorDefaultRedirect';
import { PorSectionRoute } from '../../components/plan-of-record/PorSectionRoute';
import { PorPageLayoutEditor } from '../../components/plan-of-record/PorPageLayoutEditor';

export default function PorWorkspace({ porId }: { porId?: string }) {
  const { id: routeId } = useParams<{ id: string }>();
  const id = porId ?? routeId;
  const location = useLocation();
  const narrativeUnified = usePlanNarrativeEnabled();
  const { basePath, instrumentListHref } = usePlanInstrumentNav();
  const [assembled, setAssembled] = useState<AssembledPor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    const { data, error: err } = await assemblePor(id);
    if (err) {
      setError(err);
      setLoading(false);
      return;
    }
    if (!data || data.success === false) {
      setError(data && data.success === false ? data.error : 'Plan of Record not found.');
      setLoading(false);
      return;
    }
    setAssembled(data);
    setError(null);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const refresh = useCallback(async () => {
    await load();
  }, [load]);

  // One route per surface (§2.6): with the unified PoR surface on, a
  // cycle-linked record's standalone URL redirects to the plan's narrative
  // subpage, preserving the face (synopsis, official, section/…). Only fires
  // on the standalone mount — the subpage passes porId as a prop.
  if (
    porId == null &&
    narrativeUnified &&
    assembled?.record.plan_cycle_id &&
    id != null
  ) {
    const subpath = instrumentSubpathFromPathname(location.pathname, basePath, id);
    if (subpath !== null) {
      const base = planNarrativeBasePath(assembled.record.plan_cycle_id);
      return <Navigate to={subpath ? `${base}/${subpath}` : base} replace />;
    }
  }

  if (loading) {
    return (
      <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-6 py-16 text-center">
        <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">Loading Plan of Record…</p>
      </div>
    );
  }

  if (error || !assembled) {
    return (
      <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-6 py-14 text-center max-w-lg mx-auto">
        <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">Could not open this Plan of Record</p>
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-2">{error}</p>
        <Link
          to={instrumentListHref}
          className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 mt-6 inline-block"
        >
          ← Back to all Plans of Record
        </Link>
      </div>
    );
  }

  return (
    <PorLayout
      sidebar={
        <PorSidebar porId={assembled.id} title={assembled.record.title} sections={assembled.sections} />
      }
    >
      <Routes>
        <Route index element={<PorDefaultRedirect porId={assembled.id} />} />
        <Route
          path="dashboard"
          element={
            <PorVisiblePage porId={assembled.id} pageKey="dashboard">
              <PorDashboard assembled={assembled} refresh={refresh} />
            </PorVisiblePage>
          }
        />
        <Route
          path="section/:sectionType"
          element={
            <PorSectionRoute porId={assembled.id}>
              <PorSectionView assembled={assembled} refresh={refresh} />
            </PorSectionRoute>
          }
        />
        <Route
          path="priorities"
          element={
            <PorVisiblePage porId={assembled.id} pageKey="priorities">
              <PorStrategicPriorities assembled={assembled} />
            </PorVisiblePage>
          }
        />
        <Route
          path="change-log"
          element={
            <PorVisiblePage porId={assembled.id} pageKey="change_log">
              <PorChangeLog assembled={assembled} />
            </PorVisiblePage>
          }
        />
        <Route
          path="layout"
          element={<PorPageLayoutEditor porId={assembled.id} title={assembled.record.title} />}
        />
        {/* Audience-shaped read views — always available, deliberately outside
            the page-visibility settings system. Each is a shareable URL. */}
        <Route path="synopsis" element={<PorSynopsis assembled={assembled} />} />
        <Route path="sponsor" element={<PorSponsorBrief assembled={assembled} />} />
        {/* Official record — renders the FROZEN por_record_snapshot, not live
            rows. Latest at /official; historical versions addressable. */}
        <Route path="official" element={<PorOfficialRecord porId={assembled.id} />} />
        <Route path="official/:version" element={<PorOfficialRecord porId={assembled.id} />} />
        <Route path="*" element={<PorDefaultRedirect porId={assembled.id} />} />
      </Routes>
    </PorLayout>
  );
}
